import merge from 'deepmerge'
import type { Instance, SnapshotOut } from 'mobx-state-tree'
import { types } from 'mobx-state-tree'
import type { ComponentProps, ComponentPropsWithoutRef, ComponentType, ElementType } from 'react'

import type { DeepPartial } from '../../types/helpers'
import { deepMergeOverwriteArray, getObjectValues } from '../../utils/objects'
import { withRootStore } from './helpers/withRootStore'
import { withSetProp } from './helpers/withSetProp'

export enum ModalStatus {
  Open = 'open',
  Closed = 'closed',
}

export enum GlobalLoadingOverlayStatus {
  Fullscreen = 'fullscreen',
  Relative = 'relative',
  Closed = 'closed',
}

export enum InventoryTabs {
  Assets = 'assets',
  Groups = 'groups',
  Trackers = 'trackers',
  Targets = 'targets',
}

export const UIStoreModel = types
  .model('UIStore')
  .extend(withRootStore)
  .props({
    devModalStatus: types.optional(types.enumeration<ModalStatus>(getObjectValues(ModalStatus)), ModalStatus.Closed),
    searchModalStatus: types.optional(types.enumeration<ModalStatus>(getObjectValues(ModalStatus)), ModalStatus.Closed),
    globalLoadingOverlay: types.optional(types.frozen<GlobalLoadingOverlay>(), {
      type: GlobalLoadingOverlayStatus.Closed,
    }),
    tableHeightState: types.optional(types.frozen<Record<string, 'auto' | 'full'>>(), {}),
    showAppBanner: types.optional(types.boolean, true),
  })
  .extend(withSetProp)
  .volatile<UIStoreVolatileProps>(() => ({
    tableModals: [],
    formDrawers: [],
    isEditingTableCell: false,
  }))
  .actions(self => ({
    toggleTableHeight(key: string) {
      self.tableHeightState = {
        ...self.tableHeightState,
        [key]: self.tableHeightState[key] === 'full' ? 'auto' : 'full',
      }
    },
    setIsEditingTableCell(isEditingTableCell: boolean) {
      self.isEditingTableCell = isEditingTableCell
    },
    setTableModals(tableModals: UIStoreVolatileProps['tableModals']) {
      self.tableModals = tableModals
    },
    setFormDrawers(formDrawers: UIStoreVolatileProps['formDrawers']) {
      self.formDrawers = formDrawers
    },
    showLoadingOverlay(
      status: Exclude<`${GlobalLoadingOverlayStatus}`, 'closed'> = 'fullscreen',
      opts?: GlobalLoadingOverlay['opts'],
    ) {
      self.globalLoadingOverlay = { type: status, opts }
    },
    updateLoadingOverlay(opts: GlobalLoadingOverlay['opts']) {
      self.globalLoadingOverlay = { ...self.globalLoadingOverlay, opts }
    },
    hideLoadingOverlay() {
      self.globalLoadingOverlay = { type: GlobalLoadingOverlayStatus.Closed }
    },
    openTableModal<T extends ComponentType>(modal: TableModal<T>) {
      this.setTableModals([...self.tableModals, modal])
    },
    updateTableModal<T extends ComponentType>(
      modal: DeepPartial<Omit<TableModal<T>, 'id' | 'Component'>> & { id: string },
    ) {
      if (modal.id) {
        const modalIndex = self.tableModals.findIndex(({ id }) => id === modal.id)

        if (modalIndex !== -1) {
          const m = self.tableModals[modalIndex]
          const mergeOpts = { arrayMerge: deepMergeOverwriteArray }
          m.ComponentProps = merge(m.ComponentProps, modal.ComponentProps ?? {}, mergeOpts)
          m.ModalProps = merge(m.ModalProps ?? {}, modal.ModalProps ?? {}, mergeOpts)
          m.TitleProps = merge(m.TitleProps ?? {}, modal.TitleProps ?? {}, mergeOpts) as TableModal<T>['TitleProps']

          this.setTableModals([...self.tableModals.slice(0, modalIndex), m, ...self.tableModals.slice(modalIndex + 1)])
        }
      }
    },
    closeTableModal(opts?: { closeAll?: boolean }) {
      const { closeAll = false } = opts || {}

      if (self.tableModals.length === 0) return

      if (closeAll) {
        this.setTableModals([])
      } else {
        this.setTableModals([...self.tableModals.slice(0, -1)])
      }
    },
    openFormDrawer<T extends ComponentType>(form: FormDrawer<T>) {
      this.setFormDrawers([...self.formDrawers, form])
    },
    closeFormDrawer(opts?: { closeAll?: boolean }) {
      const { closeAll = false } = opts || {}

      if (self.formDrawers.length === 0) return

      if (closeAll) {
        this.setFormDrawers([])
      } else {
        this.setFormDrawers([...self.formDrawers.slice(0, -1)])
      }
    },
    reset() {
      self.showAppBanner = true
      self.devModalStatus = ModalStatus.Closed
      self.searchModalStatus = ModalStatus.Closed
      self.globalLoadingOverlay = { type: GlobalLoadingOverlayStatus.Closed }
      self.tableHeightState = {}
    },
  }))

type TableModal<T extends ComponentType = ComponentType> = {
  id?: string
  Component: T
  ComponentProps: ComponentProps<T>
  ModalProps?: ComponentPropsWithoutRef<ElementType>
  TitleProps?: { Icon: ComponentType; title: string }
}

type FormDrawer<T extends ComponentType = ComponentType> = {
  id?: string
  Component: T
  ComponentProps: ComponentProps<T>
  DrawerProps?: ComponentPropsWithoutRef<ElementType>
  TitleProps?: { Icon: ComponentType; title: string }
}

type GlobalLoadingOverlay = {
  type: `${GlobalLoadingOverlayStatus}`
  opts?: {
    helper?: string
  }
}

export interface UIStoreVolatileProps {
  isEditingTableCell: boolean
  tableModals: Array<TableModal>
  formDrawers: Array<FormDrawer>
}

export interface UIStore extends Instance<typeof UIStoreModel> {}
export interface UIStoreSnapshot extends SnapshotOut<typeof UIStoreModel> {}
